import { cn } from 'src/lib/utils';
import { Skeleton } from 'src/components/ui/skeleton';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import PacingBoardStatusBadge from './pacing-board-status-badge';
import type { PacingBudget } from '../../types/pacing-board.types';

function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num || 0);
}

function BudgetRailSkeletonItem() {
  return (
    <div
      className="border-b border-[#EDEEF2] px-4 py-3"
      aria-hidden
    >
      <Skeleton className="mb-2 h-4 w-3/4" />
      <Skeleton className="mb-3 h-3 w-1/2" />
      <div className="mb-2 flex items-center justify-between gap-3">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-3 w-14" />
      </div>
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

interface PacingBudgetRailProps {
  budgets: PacingBudget[];
  selectedBudgetId: string | null;
  onSelect: (budgetId: string) => void;
  canEdit: boolean;
  onAddBudget: () => void;
  isRefreshing?: boolean;
}

export default function PacingBudgetRail({
  budgets,
  selectedBudgetId,
  onSelect,
  canEdit,
  onAddBudget,
  isRefreshing = false,
}: PacingBudgetRailProps) {
  const showEmpty = budgets.length === 0 && !isRefreshing;

  return (
    <div className="flex h-full w-[300px] shrink-0 flex-col border-r border-[#EDEEF2] bg-white">
      <div className="flex items-center justify-between border-b border-[#EDEEF2] px-4 py-3">
        <span className={portalDs.sectionLabel}>Budgets</span>
        {canEdit && (
          <button
            type="button"
            onClick={onAddBudget}
            className="text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8]"
          >
            + Add
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {showEmpty ? (
          <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">
            No budgets yet.
            {canEdit && ' Add your first budget to start tracking.'}
          </div>
        ) : (
          <>
            {budgets.map((budget) => {
              const detail = budget.detail;
              const isSelected = budget.id === selectedBudgetId;
              const spendPct = detail && parseFloat(budget.net_budget) > 0
                ? Math.round((detail.actual_spend / parseFloat(budget.net_budget)) * 100)
                : 0;

              return (
                <button
                  key={budget.id}
                  type="button"
                  onClick={() => onSelect(budget.id)}
                  className={cn(
                    'w-full border-b border-[#EDEEF2] px-4 py-3 text-left transition-colors',
                    isSelected ? 'bg-[#EFF6FF]' : 'hover:bg-[#F0F1F5]',
                  )}
                >
                  <div className="mb-1 truncate text-sm font-semibold text-[#0D0F1A]">
                    {budget.campaign_name}
                  </div>
                  <div className="mb-2 truncate text-xs text-[#5E6278]">
                    {budget.client_name_display || 'No client'}
                  </div>
                  <div className="mb-2 flex items-center justify-between">
                    <PacingBoardStatusBadge status={detail?.status} />
                    <span className="text-xs font-medium text-[#5E6278]">{spendPct}% spent</span>
                  </div>
                  <div className="text-xs text-[#9CA3AF]">
                    {formatCurrency(detail?.actual_spend ?? 0)} / {formatCurrency(budget.net_budget)}
                  </div>
                </button>
              );
            })}
            {isRefreshing && (
              <div role="status" aria-live="polite" aria-label="Loading budgets">
                <BudgetRailSkeletonItem />
                {budgets.length === 0 && <BudgetRailSkeletonItem />}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
